import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { disponible, sePuedeVender, frase, costoDelDia } from '../utils/leche_disponible';
import Remision from './Remision';

// Vender.
//
// Dos formas de vender que no se parecen en nada, en una pantalla que cambia
// de forma según cuál sea:
//
//   MOSTRADOR  hay que hacerlo en diez segundos con el cliente esperando.
//              Sin buscar cliente, sin elegir precio, sin preguntar si fía.
//   POR MAYOR  al revés: cliente, su precio, contado o crédito, comprobante.
//
// Meter las dos en el mismo formulario hace que la de mostrador sea lenta y la
// de mayoreo sea incompleta. Por eso el canal es lo primero que se elige y lo
// que decide qué se ve.
function Vender() {
  // Se recuerda el último canal usado. Hay negocios que venden casi todo al
  // por mayor —el comprador de agosto es uno— y arrancar siempre en Mostrador
  // son dos toques de más en cada venta del día. No es un ajuste que haya que
  // configurar: se aprende solo.
  const [canal, setCanal] = useState(
    () => localStorage.getItem('quesera.canal') === 'mayor' ? 'mayor' : 'detal');

  useEffect(() => { localStorage.setItem('quesera.canal', canal); }, [canal]);
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [inventario, setInventario] = useState([]);

  const [carrito, setCarrito] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [formaPago, setFormaPago] = useState('contado');
  const [aviso, setAviso] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [remision, setRemision] = useState(null);
  // La leche del día. La leche cruda NO tiene lote —no sale de una tina— así
  // que su existencia es la que entró hoy y todavía no se cuajó ni se vendió.
  const [lecheDia, setLecheDia] = useState(null);
  // EN QUÉ DÍA SE ANOTA ESTA VENTA.
  //
  // Antes era siempre hoy, sin decirlo. Y la leche que se lleva Juan Pablo no
  // siempre se alcanza a anotar el mismo día: cuando el cliente se puso al día
  // con los registros de los días pasados, esa leche no hubo forma de
  // descontarla —quedaba anotada hoy, o no quedaba—.
  const [fechaVenta, setFechaVenta] = useState(hoy());

  // Producto que se está pesando ahora mismo
  const [pesando, setPesando] = useState(null);
  const [cantidad, setCantidad] = useState('');
  const [precioLinea, setPrecioLinea] = useState('');

  const cargar = useCallback(() => {
    axios.get('/api/productos').then(r => setProductos(r.data || [])).catch(() => {});
    axios.get('/api/clientes').then(r => setClientes(r.data || [])).catch(() => {});
    axios.get('/api/inventario').then(r => setInventario(r.data || [])).catch(() => {});
    // La leche del DÍA QUE SE ESTÁ ANOTANDO, no la de hoy. Vendiendo con fecha
    // del 20 hay que mirar la leche del 20: contra la de hoy diría que no
    // alcanza —o peor, que sí alcanza cuando no—.
    axios.get(`/api/leche/dia?fecha=${fechaVenta}`)
      .then(r => setLecheDia(r.data || null)).catch(() => setLecheDia(null));
  }, [fechaVenta]);

  useEffect(cargar, [cargar]);

  const cliente = clientes.find(c => String(c.id) === String(clienteId));

  // Si a este cliente ya se le hizo su descuento en el periodo de esta fecha.
  // Lo contesta el servidor, que es el único que sabe qué ventas hay guardadas.
  const [yaSeDescontó, setYaSeDescontó] = useState(false);
  const [periodoDesc, setPeriodoDesc] = useState(null);
  useEffect(() => {
    if (!clienteId || !(Number(cliente && cliente.descuento_kilos) > 0)) {
      setYaSeDescontó(false); setPeriodoDesc(null); return;
    }
    let vigente = true;
    axios.get(`/api/clientes/${clienteId}/descuento-del-periodo?fecha=${fechaVenta}`)
      .then(r => {
        if (!vigente) return;
        setYaSeDescontó(!!(r.data && r.data.ya_usado));
        setPeriodoDesc((r.data && r.data.periodo) || null);
      })
      // Si no se puede preguntar, se supone que NO se le ha hecho: es como se
      // comportaba antes y el servidor corrige al guardar. Suponer lo contrario
      // dejaría de descontarle a quien sí le toca.
      .catch(() => { if (vigente) { setYaSeDescontó(false); setPeriodoDesc(null); } });
    return () => { vigente = false; };
  }, [clienteId, fechaVenta, cliente]);

  // Lo que hay de cada variedad, sumando sus lotes. Se enseña en el botón para
  // no vender a ciegas lo que ya no queda.
  // Lo que ya está en el carrito, para no vender dos veces la misma leche en
  // la misma venta. Con el queso no hace falta —el lote se descuenta al
  // guardar— pero la leche no tiene lote que descontar.
  const enCarritoDe = (productoId) => carrito
    .filter(l => l.producto_id === productoId)
    .reduce((s, l) => s + (Number(l.kilos) || 0), 0);

  const disp = disponible(lecheDia || {});
  // A cuánto costó el litro hoy. La leche cruda se vende a precio variable, y
  // este número al lado de la casilla es lo único que evita venderla por debajo
  // de lo que se pagó por ella.
  const costo = costoDelDia((lecheDia || {}).recibos);

  const existenciaDe = (productoId) => {
    const p = productos.find(x => x.id === productoId);
    // La leche cruda no sale de un lote: lo que hay es la del día que no entró
    // a las tinas ni se vendió ya.
    if (p && p.es_leche) {
      return Math.max(0, sePuedeVender(disp, p.es_leche) - enCarritoDe(productoId));
    }
    return inventario
      .filter(l => l.producto_id === productoId)
      .reduce((s, l) => s + l.kilos, 0);
  };

  // El precio que corresponde según el canal. Al mostrador se cobra el de
  // detal; al mayorista, el de mayor.
  const precioDe = (p) => (canal === 'detal' ? p.precio_detal : p.precio_mayor) || 0;

  // CREAR EL PRODUCTO DE LECHE CRUDA, desde donde hace falta.
  //
  // Todo lo de la leche de Juan Pablo cuelga de que exista un producto marcado
  // como leche. Sin él, el cuadre del día no aparece, la leche no se puede
  // vender y no se descuenta de la que entra a las tinas — el programa se ve
  // igual que si la función no existiera.
  //
  // Y eso fue justo lo que pasó: el cliente estuvo días anotando leche y la de
  // Juan Pablo nunca se descontó. No se le olvidó: no había por dónde.
  //
  // El aviso va AQUÍ, en la pantalla donde se vende, y no en un manual. Quien
  // busca vender leche está mirando esta lista, no otra.
  const crearProductoDeLeche = async () => {
    const precio = window.prompt(
      '¿A cómo vende el litro de leche cruda?\n\n' +
      'Es solo para arrancar: en cada venta se puede cambiar, y la pantalla le ' +
      'muestra a cuánto le costó el litro ese día para que no la venda por debajo.',
      '');
    if (precio === null) return;                     // se arrepintió
    const v = Math.max(0, Math.round(Number(String(precio).replace(/[^\d]/g, '')) || 0));
    try {
      await axios.post('/api/productos', {
        nombre: 'Leche cruda de vaca', unidad: 'L', es_leche: 'vaca',
        precio_mayor: v, precio_detal: v, activo: 1,
      });
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo crear');
    }
  };

  const empezarAPesar = (p) => {
    setPesando(p);
    setCantidad('');
    setPrecioLinea(String(Math.round(precioDe(p))));
  };

  const agregar = (e) => {
    e.preventDefault();
    const cant = parseFloat(cantidad);
    const precio = parseFloat(precioLinea);
    if (!(cant > 0)) return;
    if (!(precio > 0)) return setAviso({ tipo: 'malo', texto: 'Falta el precio de ' + pesando.nombre });

    // Kilos y litros se miden; las piezas se cuentan.
    const porMedida = pesando.unidad === 'kg' || pesando.unidad === 'L';

    setCarrito([...carrito, {
      clave: Date.now(),
      producto_id: pesando.id,
      nombre: pesando.nombre,
      unidad: pesando.unidad,
      // LO QUE SE MIDE VA EN `kilos`; lo que se cuenta, en `piezas`.
      //
      // Antes la regla era «kg o piezas», y la leche —que se vende por LITRO—
      // caía del lado de las piezas: la venta quedaba guardada con cantidad
      // CERO en kilos. El total salía bien, así que en pantalla no se notaba
      // nada; pero todo el descuento de la leche del día lee `kilos`, así que
      // la leche que se llevó Juan Pablo nunca se descontaba de la que entra a
      // las tinas. Se veía igual que si no se hubiera anotado.
      //
      // El servidor ya lo tenía bien: con kilos > 0 y producto de leche, la
      // registra sin lote y sin avisar. El que estaba mal era este renglón.
      kilos: porMedida ? cant : 0,
      piezas: porMedida ? 0 : cant,
      precio,
      total: Math.round(cant * precio)
    }]);
    setPesando(null);
    setAviso(null);
  };

  const quitar = (clave) => setCarrito(carrito.filter(l => l.clave !== clave));

  const subtotal = carrito.reduce((s, l) => s + l.total, 0);

  // ── EL DESCUENTO DE KILOS DEL CLIENTE ────────────────────────────────────
  //
  // «A Víctor se le descuentan 3 kilos: si se le venden 100, se le cobran 97.»
  //
  // Se calcula igual que en el servidor —al precio de la línea con más kilos—
  // pero SOLO para enseñarlo antes de cobrar. El que manda es el del servidor:
  // así no hay forma de rebajar una cuenta tecleando un número aquí.
  //
  // Se enseña ANTES de cobrar y no solo en el recibo: el vendedor tiene que
  // poder decirle al cliente cuánto le van a cobrar, no enterarse después.
  // Y SOLO SI NO SE LE HIZO YA EN ESTE PERIODO.
  //
  // El descuento es de la FACTURA, no del viaje: cae en el primer despacho del
  // periodo y los demás salen sin él. Sin preguntarlo, esta pantalla enseñaría
  // «menos $58.500» en los cuatro despachos de la semana de Víctor mientras el
  // servidor solo se lo hace al primero — el vendedor le diría al cliente un
  // precio y el papel diría otro.
  //
  // Se pregunta por la FECHA DE LA VENTA, no por hoy: vendiendo con fecha
  // atrasada, el periodo que cuenta es el de esa fecha.
  const kilosPactados = Math.max(0, Number(cliente && cliente.descuento_kilos) || 0);
  const kilosVendidos = carrito.reduce((a, l) => a + (Number(l.kilos) || 0), 0);
  const principal = carrito.filter(l => l.kilos > 0)
                           .sort((a, b) => b.kilos - a.kilos)[0];
  const kilosDesc = principal && !yaSeDescontó ? Math.min(kilosPactados, kilosVendidos) : 0;
  const descuento = kilosDesc > 0 ? Math.round(kilosDesc * principal.precio) : 0;
  const total = Math.max(0, subtotal - descuento);

  const mostrarRemision = async (id, forma, cli) => {
    try {
      const { data } = await axios.get('/api/ventas');
      const v = (data || []).find(x => x.id === id);
      if (!v) return;
      setRemision({
        serie: 'REM',
        numero: v.id, fecha: v.fecha, total: v.total, forma_pago: v.forma_pago || forma,
        descuento: v.descuento, descuento_kilos: v.descuento_kilos,
        cliente: v.cliente || cli?.nombre || null,
        documento: cli?.documento || null,
        ciudad: cli?.ciudad || null, direccion: cli?.direccion || null,
        lineas: (v.lineas || []).map(l => ({
          producto: l.producto, lote: l.lote || null, unidad: l.unidad,
          kilos: l.kilos > 0 ? l.kilos : l.piezas, precio: l.precio,
        })),
      });
    } catch {
      // Si no se puede traer, la venta YA quedó guardada: no se pierde nada,
      // solo el papel. Se puede reimprimir desde «Últimas ventas».
    }
  };

  const cobrar = async () => {
    if (carrito.length === 0) return;
    if (formaPago === 'credito' && !clienteId) {
      return setAviso({ tipo: 'malo', texto: 'Para fiar hay que decir a qué cliente' });
    }

    setGuardando(true);
    setAviso(null);
    try {
      const { data } = await axios.post('/api/ventas', {
        cliente_id: clienteId ? parseInt(clienteId) : null,
        canal,
        fecha: fechaVenta,
        forma_pago: formaPago,
        lineas: carrito.map(l => ({
          producto_id: l.producto_id,
          kilos: l.kilos,
          piezas: l.piezas,
          precio: l.precio
        }))
      });

      setAviso({
        tipo: data.aviso ? 'ojo' : 'bueno',
        texto: data.aviso
          ? `Venta de ${plata(data.total)} registrada. ${data.aviso}`
          : `Venta de ${plata(data.total)} registrada` +
            (formaPago === 'credito' ? ` a nombre de ${cliente?.nombre}` : '') + '.'
      });
      // La remisión, con lo que quedó GUARDADO. Antes la venta solo dejaba un
      // aviso que se cierra de un clic: el mayorista se llevaba el queso y
      // quedaba la palabra de cada uno sobre cuántos kilos iban y a cuánto.
      //
      // Se vuelve a pedir la venta en vez de armarla con el carrito, porque el
      // LOTE lo reparte el servidor —de lo más viejo a lo más nuevo— y el
      // carrito no lo sabe. Un lote inventado en un papel firmado es peor que
      // no ponerlo: el día que un queso salga malo, ese número es el que se
      // sigue.
      mostrarRemision(data.id, formaPago, cliente);
      setCarrito([]);
      setClienteId('');
      setFormaPago('contado');
      cargar();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo registrar la venta' });
    }
    setGuardando(false);
  };

  const cambiarCanal = (nuevo) => {
    setCanal(nuevo);
    setCarrito([]);
    setPesando(null);
    setClienteId('');
    // Al mostrador se cobra de contado; fiar es cosa del mayoreo.
    setFormaPago('contado');
    setAviso(null);
  };

  return (
    <>
      <Remision v={remision} alCerrar={() => setRemision(null)} />
      <div className="encabezado">
        <h1>Vender</h1>
        <p>{canal === 'detal'
          ? 'Mostrador: pesar, cobrar, entregar'
          : 'Por mayor: a un cliente, de contado o fiado'}</p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { id: 'detal', txt: '🛒 Mostrador', pie: 'rápido, en efectivo' },
          { id: 'mayor', txt: '🚚 Por mayor', pie: 'a un cliente, con su precio' }
        ].map(o => (
          <button
            key={o.id}
            onClick={() => cambiarCanal(o.id)}
            style={{
              flex: '1 1 190px', padding: '14px 16px', textAlign: 'left', cursor: 'pointer',
              borderRadius: 'var(--radio)', font: 'inherit',
              border: canal === o.id ? '2px solid var(--acento-vivo)' : '1px solid var(--raya)',
              background: canal === o.id ? 'var(--acento-piso)' : 'var(--tarjeta)'
            }}
          >
            <div style={{ fontWeight: 700, color: canal === o.id ? 'var(--acento)' : 'var(--tinta)' }}>
              {o.txt}
            </div>
            <div style={{ fontSize: 12, color: 'var(--tinta-tenue)', marginTop: 2 }}>{o.pie}</div>
          </button>
        ))}
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      {/* LA FECHA DE LA VENTA.
          
          Va en los dos canales y con la misma regla, porque el que la llena no
          tiene por qué saber que el programa trabaja distinto según el botón
          de arriba. Y va ANTES de los productos: primero se dice de qué día
          es, y después se pesa — al revés, se pesa contra la existencia
          equivocada.
          
          Cuando no es hoy lo dice en amarillo y no se calla. Una fecha vieja
          que se quedó puesta anota toda la venta del día en el día que no es,
          y eso no se descubre hasta que las cuentas no cuadran. */}
      <div className="bloque">
        <div className="cuerpo" style={{ display: 'flex', alignItems: 'flex-end',
                                         gap: 14, flexWrap: 'wrap' }}>
          <label className="campo" style={{ margin: 0, minWidth: 200 }}>
            <span>Día de la venta</span>
            <input type="date" value={fechaVenta} max={hoy()}
                   onChange={e => setFechaVenta(e.target.value || hoy())} />
          </label>
          {fechaVenta !== hoy() ? (
            <div className="aviso ojo" style={{ margin: 0, flex: '1 1 260px' }}>
              Se va a anotar en el <strong>{fecha(fechaVenta)}</strong>, no hoy.
              La existencia y el cuadre de la leche son los de ese día.
              <button className="boton chico suave" style={{ marginLeft: 10 }}
                      onClick={() => setFechaVenta(hoy())}>
                Volver a hoy
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', paddingBottom: 10 }}>
              Cámbiela para anotar una venta de un día pasado — por ejemplo la
              leche que se llevaron y no se alcanzó a registrar.
            </div>
          )}
        </div>
      </div>

      {canal === 'mayor' && (
        <div className="bloque">
          <h2>🚚 A quién se le vende</h2>
          <div className="cuerpo">
            <div className="campos">
              <label className="campo">
                <span>Cliente {formaPago === 'credito' ? '*' : ''}</span>
                <select value={clienteId} onChange={e => setClienteId(e.target.value)}>
                  <option value="">— Elegir cliente —</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}{c.telefono ? ` · ${c.telefono}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="campo">
                <span>Cómo paga</span>
                <select value={formaPago} onChange={e => setFormaPago(e.target.value)}>
                  <option value="contado">Contado</option>
                  <option value="credito">Fiado</option>
                </select>
              </label>
            </div>
            {clientes.length === 0 && (
              <div className="aviso ojo" style={{ marginTop: 14, marginBottom: 0 }}>
                Todavía no hay clientes registrados. Se pueden agregar en la pantalla
                de Clientes, o vender de contado sin poner cliente.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>🧀 Qué se lleva</h2>
        <div className="cuerpo">
          {/* EL CUADRE DE LA LECHE DEL DÍA.
              
              Solo cuando hay leche cruda que vender: al que solo vende queso
              este renglón no le dice nada y sobra. Va aquí porque es donde se
              decide vender, y porque un descuadre hay que verlo el MISMO día —
              al cierre del mes ya no hay a quién preguntarle. */}
          {productos.some(p => p.es_leche) && lecheDia && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12,
                          fontSize: 13 }}>
              {['vaca', 'bufala'].map(t => {
                const d = disp[t];
                if (!d || (d.recibido === 0 && d.cuajado === 0)) return null;
                return (
                  <div key={t} style={{
                    padding: '8px 12px', borderRadius: 8,
                    background: d.falta > 0 ? 'var(--malo-piso)' : 'var(--hondo)',
                    color: d.falta > 0 ? 'var(--malo)' : 'var(--tinta-media)',
                  }}>
                    <strong>{t === 'bufala' ? '🐃 Búfala' : '🐄 Vaca'}</strong>{' '}
                    {/* El DÍA que se está mirando, no «hoy» a secas. Con la
                        fecha cambiada al 18, decir «hoy» convierte el cuadre
                        en una mentira: son los litros del 18. */}
                    {fechaVenta === hoy() ? 'hoy' : `el ${fecha(fechaVenta)}`}:{' '}
                    {peso(d.recibido, 'L')} recibidos · {peso(d.cuajado, 'L')} a las tinas
                    {d.vendido > 0 && <> · {peso(d.vendido, 'L')} vendidos</>}
                    {' — '}{frase(d)}
                  </div>
                );
              })}
            </div>
          )}

          {!productos.some(p => p.es_leche) && (
            <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginBottom: 12 }}>
              ¿Le vende leche cruda a alguien —sin cuajarla?{' '}
              <button className="boton chico suave" onClick={crearProductoDeLeche}>
                Créela una vez
              </button>{' '}
              y desde ahí se descuenta sola de la que entra a las tinas.
            </div>
          )}

          {/* Los productos como botones grandes. En el mostrador se toca, no se
              busca en una lista desplegable con una mano ocupada. */}
          <div style={{ display: 'grid', gap: 10,
                        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
            {productos.map(p => {
              const hay = existenciaDe(p.id);
              const precio = precioDe(p);
              return (
                <button
                  key={p.id}
                  onClick={() => empezarAPesar(p)}
                  style={{
                    padding: '14px 14px 12px', textAlign: 'left', cursor: 'pointer',
                    borderRadius: 'var(--radio)', font: 'inherit',
                    border: '1px solid var(--raya)', background: 'var(--tarjeta)',
                    boxShadow: 'var(--sombra)'
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>{p.nombre}</div>
                  <div className="cifra" style={{ color: 'var(--acento)', fontWeight: 700 }}>
                    {precio > 0 ? plata(precio) + '/' + p.unidad : 'sin precio'}
                  </div>
                  <div style={{ fontSize: 12, color: hay > 0 ? 'var(--tinta-tenue)' : 'var(--malo)',
                                marginTop: 4 }}>
                    {/* La unidad del producto, no siempre kilos: la leche va
                        en litros y decir «quedan 50 kg» de leche es falso. */}
                    {hay > 0 ? `quedan ${peso(hay, p.unidad || 'kg')}` : 'sin existencia'}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pesar lo que se acaba de tocar */}
          {pesando && (
            <form onSubmit={agregar} style={{
              marginTop: 16, padding: 16, borderRadius: 'var(--radio)',
              background: 'var(--acento-piso)', border: '1px solid var(--acento-vivo)'
            }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>
                {pesando.nombre} · {pesando.unidad === 'kg' ? 'cuánto pesó' : 'cuántas'}
              </div>
              <div className="campos">
                <label className="campo">
                  <span>{pesando.unidad === 'kg' ? 'Kilos *'
                          : pesando.unidad === 'L' ? 'Litros *' : 'Unidades *'}</span>
                  <input
                    type="number" step={pesando.unidad === 'kg' ? '0.01' : '1'}
                    min="0" className="peso" required autoFocus
                    value={cantidad} onChange={e => setCantidad(e.target.value)}
                    placeholder="0"
                  />
                </label>
                <label className="campo">
                  <span>Precio por {pesando.unidad} *</span>
                  <input type="number" step="1" min="0" className="peso" required
                         value={precioLinea} onChange={e => setPrecioLinea(e.target.value)} />
                  {/* LO QUE COSTÓ, debajo de lo que se va a cobrar.
                      
                      El queso tiene un precio pactado y ahí el costo no hace
                      falta a la hora de vender. La leche cruda se vende al
                      precio que se acuerde cada vez, y sin el costo al lado no
                      hay forma de saber si se está vendiendo con pérdida hasta
                      que se cierra el mes. */}
                  {pesando.es_leche && costo[pesando.es_leche] !== null && (() => {
                    const c = costo[pesando.es_leche];
                    const pv = parseFloat(precioLinea) || 0;
                    const debajo = pv > 0 && pv < c;
                    return (
                      <small style={{ color: debajo ? 'var(--malo)' : 'var(--tinta-tenue)',
                                      fontWeight: debajo ? 700 : 400 }}>
                        {/* El día que se está anotando, no «hoy». Con la
                            fecha en el 18, ese costo es el del 18. */}
                        {fechaVenta === hoy() ? 'Hoy' : `El ${fecha(fechaVenta)}`} le
                        costó {plata(c)} el litro.
                        {debajo && <> Lo está vendiendo {plata(c - pv)} por debajo.</>}
                        {!debajo && pv > c && <> Le deja {plata(pv - c)} por litro.</>}
                      </small>
                    );
                  })()}
                </label>
              </div>
              {parseFloat(cantidad) > 0 && parseFloat(precioLinea) > 0 && (
                <div style={{ marginTop: 12, fontSize: 15 }}>
                  {peso(cantidad, pesando.unidad)} × {plata(precioLinea)} ={' '}
                  <strong className="cifra">
                    {plata(parseFloat(cantidad) * parseFloat(precioLinea))}
                  </strong>
                </div>
              )}
              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setPesando(null)}>
                  Cancelar
                </button>
                <button className="boton">➕ Agregar</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {carrito.length > 0 && (
        <div className="bloque">
          <h2>
            🧾 La cuenta
            <span className="contador">{carrito.length} {carrito.length === 1 ? 'línea' : 'líneas'}</span>
          </h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {carrito.map(l => (
                  <tr key={l.clave}>
                    <td className="recio">{l.nombre}</td>
                    <td className="num">
                      {l.kilos > 0 ? peso(l.kilos, l.unidad || 'kg') : l.piezas + ' u'}
                    </td>
                    <td className="num">{plata(l.precio)}</td>
                    <td className="num recio">{plata(l.total)}</td>
                    <td>
                      <button className="boton chico suave" onClick={() => quitar(l.clave)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cuerpo" style={{ borderTop: '1px solid var(--raya)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                          gap: 16, flexWrap: 'wrap' }}>
              <div>
                {/* La cuenta a la vista cuando hay descuento: sin los tres
                    renglones, el total sale más bajo que la suma de las líneas
                    y parece un error del programa. */}
                {descuento > 0 && (
                  <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginBottom: 6 }}>
                    {plata(subtotal)} · menos {peso(kilosDesc, 'kg')} de descuento
                    a {cliente.nombre} ={' '}
                    <strong style={{ color: 'var(--bueno)' }}>−{plata(descuento)}</strong>
                  </div>
                )}
                {/* POR QUÉ ESTE NO LLEVA DESCUENTO.

                    Sin decirlo, el vendedor ve que al primer despacho de la
                    semana se le rebajó y a éste no, y lo lee como que el
                    programa se equivocó. Decir cuál factura ya se lo llevó
                    contesta la pregunta antes de que la haga. */}
                {yaSeDescontó && kilosPactados > 0 && carrito.length > 0 && (
                  <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginBottom: 6 }}>
                    Sin descuento: los {peso(kilosPactados, 'kg')} de {cliente.nombre}{' '}
                    ya se le hicieron en esta factura
                    {periodoDesc
                      ? ` (${fecha(periodoDesc.desde)} al ${fecha(periodoDesc.hasta)})`
                      : ''}.
                  </div>
                )}
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>Total a cobrar</div>
                <div className="cifra" style={{ fontSize: 30, fontWeight: 800,
                                                letterSpacing: '-.03em', color: 'var(--venta)' }}>
                  {plata(total)}
                </div>
                {descuento > 0 && (
                  <div style={{ fontSize: 12.5, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                    Se lleva los {peso(kilosVendidos, 'kg')} completos.
                  </div>
                )}
                {formaPago === 'credito' && cliente && (
                  <div style={{ fontSize: 13, color: 'var(--ojo)', marginTop: 4 }}>
                    Queda debiendo {cliente.nombre}
                  </div>
                )}
              </div>
              <button className="boton" disabled={guardando}
                      style={{ fontSize: 16, padding: '14px 26px' }}
                      onClick={cobrar}>
                {guardando ? 'Guardando…'
                  : formaPago === 'credito' ? '📝 Anotar fiado' : '💵 Cobrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UltimasVentas />
    </>
  );
}

// El historial va aparte para que volver a cargarlo no repinte el carrito a
// medio llenar.
function UltimasVentas() {
  const [remision, setRemision] = useState(null);
  const [ventas, setVentas] = useState([]);

  const cargar = useCallback(() => {
    axios.get('/api/ventas').then(r => setVentas(r.data || [])).catch(() => {});
  }, []);
  useEffect(cargar, [cargar]);

  const anular = async (v) => {
    if (!window.confirm(
      `¿Anular la venta de ${plata(v.total)}?\n\n` +
      'La mercancía vuelve al inventario y deja de contar en la cartera.'
    )) return;
    try {
      await axios.post(`/api/ventas/${v.id}/anular`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo anular');
    }
  };

  if (ventas.length === 0) return null;

  return (
    <div className="bloque">
      <Remision v={remision} alCerrar={() => setRemision(null)} />
      <h2>📋 Últimas ventas <span className="contador">{ventas.length}</span></h2>
      <div className="tabla-caja">
        <table className="datos">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Canal</th>
              <th>Cliente</th>
              <th>Qué se llevó</th>
              <th>Pago</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ventas.slice(0, 40).map(v => (
              <tr key={v.id} style={{ opacity: v.anulada ? .45 : 1 }}>
                <td>{fecha(v.fecha)}</td>
                <td>
                  <span className="pinta gris">{v.canal === 'mayor' ? 'Por mayor' : 'Mostrador'}</span>
                </td>
                <td>{v.cliente || <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}</td>
                <td style={{ fontSize: 13 }}>
                  {v.lineas.map(l => (
                    <div key={l.id}>
                      {l.producto} {l.kilos > 0 ? peso(l.kilos, 'kg') : l.piezas + ' u'}
                      {l.lote && <span style={{ color: 'var(--tinta-tenue)' }}> · lote {l.lote}</span>}
                    </div>
                  ))}
                </td>
                <td>
                  {v.anulada
                    ? <span className="pinta malo">Anulada</span>
                    : v.forma_pago === 'credito'
                      ? <span className="pinta ojo">Fiado</span>
                      : <span className="pinta bueno">Contado</span>}
                </td>
                <td className="num recio">{plata(v.total)}</td>
                <td>
                  {!v.anulada && (
                    <button className="boton chico suave" onClick={() => anular(v)}>Anular</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Vender;
